// gioco/usaVoce.ts — v026: la voce buffa scelta in partita.
// v046 (il proprietario: «chi entra deve capire subito che può cambiare voce»): si parte già con una voce buffa —
// quella scelta l'ultima volta o, la prima volta, una a caso tra quelle simpatiche (voceIniziale) — se il pannello lo
// lascia (app.voce_iniziale). I browser accendono l'audio solo dopo un tocco: finché è fermo si manda la voce vera e al
// primo tocco sullo schermo parte quella buffa (niente silenzio per l'altro).
import { useEffect, useMemo, useState } from "react";
import { LaboratorioVoce, eVoce, voceIniziale, type Voce } from "./voce.ts";
import { leggiPreferenza, salvaPreferenza } from "../servizi/preferenze.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { IMP_APP, PREFERENZE } from "../costanti.ts";

export interface StatoVoce {
  voce: Voce;
  scegli: (v: Voce) => void;
  /** La traccia audio da mandare agli altri (null = nessun microfono). */
  traccia: MediaStreamTrack | null;
  disponibile: boolean;
}

/** Lo stream da mandare: il video di `video` con l'audio scelto. */
export function conVoce(video: MediaStream, audio: MediaStreamTrack | null): MediaStream {
  return new MediaStream([...video.getVideoTracks(), ...(audio ? [audio] : video.getAudioTracks())]);
}

/** La voce con cui si comincia: l'ultima scelta, altrimenti una buffa a caso (o la normale, se il pannello dice così). */
function voceDiPartenza(): Voce {
  const salvata = leggiPreferenza(PREFERENZE.voce);
  if (eVoce(salvata)) return salvata;
  return leggiImpostazione(IMP_APP.voceIniziale, true, eBooleano) ? voceIniziale() : "normale";
}

export function useVoce(flusso: MediaStream): StatoVoce {
  const mic = flusso.getAudioTracks()[0] ?? null;
  const lab = useMemo(() => (mic && typeof window !== "undefined" && "AudioContext" in window ? new LaboratorioVoce(mic) : null), [mic]);
  const [voce, setVoce] = useState<Voce>(voceDiPartenza);
  // cambia quando l'audio del browser si accende (si rifà la traccia)
  const [acceso, setAcceso] = useState(0);
  const traccia = useMemo(() => {
    if (!lab) return mic;
    const t = lab.traccia(voce);
    return lab.sospeso() ? mic : t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lab, voce, mic, acceso]);
  useEffect(() => {
    if (!lab || !lab.sospeso()) return;
    let vivo = true;
    const prova = () => void lab.riprendi().then((ok) => vivo && ok && setAcceso((n) => n + 1));
    prova();
    window.addEventListener("pointerdown", prova, { capture: true });
    window.addEventListener("keydown", prova, { capture: true });
    return () => {
      vivo = false;
      window.removeEventListener("pointerdown", prova, { capture: true });
      window.removeEventListener("keydown", prova, { capture: true });
    };
  }, [lab, voce, acceso]);
  useEffect(() => () => lab?.chiudi(), [lab]);
  const scegli = (v: Voce) => {
    salvaPreferenza(PREFERENZE.voce, v);
    setVoce(v);
  };
  return { voce, scegli, traccia, disponibile: lab !== null };
}
